import PropTypes from 'prop-types'
import { useRef } from 'react'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import {
  Container,
  SectionTitle,
  Section,
  Button,
  Inline,
  ListItem,
} from 'chaoskit/src/components'
import { motion } from 'framer-motion'

import Quote from '~components/Quote'
import Image from '~components/Image'
import BaseLayout from '~layouts/BaseLayout'
import { stickyIt } from '~styles/utility'
import Data from '~components/Data'
import Hero, { HeroTitle, HeroTitleSub } from '~partials/Hero'
import HelmetSEO from '~partials/global/HelmetSEO'
import {
  HomeHeroGrid,
  HomeHeroGridImage,
  HomeHeroGridContent,
} from '~partials/home/Hero'
import AnimatedHero from '~media/certification/AnimatedHero'
import { ReactComponent as GridSVG } from '~media/certification/grid.svg'
import { ReactComponent as BackgroundSVG } from '~media/certification/background.svg'
import useReveal from '~hooks/useReveal'

const LeftSection = ({ heading, blurb }) => (
  <Section
    space="base"
    css={{
      maxWidth: 600,
      marginBottom: `10px !important`,
      paddingTop: `50px !important`,
    }}
  >
    <SectionTitle
      align={{ medium: 'left' }}
      title={<Data>{heading}</Data>}
      sub={<Data>{blurb}</Data>}
    />
  </Section>
)
LeftSection.propTypes = {
  heading: PropTypes.string.isRequired,
  blurb: PropTypes.string.isRequired,
}

const GremlinSeries = ({ heading, blurb, items }) => {
  const theme = useTheme()

  const ref = useRef()

  const { variants, controls } = useReveal({ ref })

  return (
    <Section
      id="gremlin-series"
      ref={ref}
      variants={variants}
      animate={controls}
      css={{
        paddingBottom: `120px !important`,
        [theme.mq.small__max]: {
          paddingBottom: `40px !important`,
        },
      }}
    >
      <div
        css={{
          display: 'grid',
          gap: theme.space.large,
          [theme.mq.medium]: {
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
            gap: theme.space.xlarge,
          },
        }}
      >
        <div
          css={{
            [theme.mq.medium]: {
              gridColumn: '1 / span 5',
              gridRow: 1,
            },
          }}
        >
          <div css={{ [theme.mq.medium]: stickyIt(theme) }}>
            <SectionTitle
              as="h2"
              align={{ medium: 'left' }}
              title={<Data>{heading}</Data>}
              sub={<Data>{blurb}</Data>}
              css={{
                // @NOTE Force line-break on larger screens and remove on mobile
                [theme.mq.small__max]: {
                  br: {
                    display: 'none',
                  },
                },
              }}
            />
          </div>
        </div>
        <div
          css={{
            display: 'grid',
            gap: theme.space.large,

            [theme.mq.medium]: {
              gridColumn: '6 / span 7',
              gridRow: 1,
            },
          }}
        >
          {items.map((serie) => (
            <Serie key={serie.heading} serie={serie} />
          ))}
        </div>
      </div>
    </Section>
  )
}
GremlinSeries.propTypes = {
  heading: PropTypes.string.isRequired,
  blurb: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      blurb: PropTypes.string.isRequired,
      heading: PropTypes.string.isRequired,
      items: PropTypes.array.isRequired,
    })
  ),
}

const Serie = ({ serie }) => {
  const theme = useTheme()

  const ref = useRef()

  const { variants, controls } = useReveal({ ref })

  return (
    <a href={serie.link} css={{ color: '#333', '& :hover': { color: '#333' } }}>
      <motion.div
        ref={ref}
        variants={variants}
        animate={controls}
        css={{
          display: 'grid',
          justifyContent: 'start',
          gap: theme.space.base,
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,

          '&::before': {
            content: "''",
            border: theme.border.base,
            top: -theme.space.base,
            left: -theme.space.base,
            right: -theme.space.base,
            bottom: -theme.space.base,
            position: 'absolute',
            zIndex: -1,
            boxShadow: theme.boxShadow.base,
            borderRadius: theme.borderRadius.base,
            opacity: 0,
            transition: `transform ${theme.timing.base} ${theme.transition.bounce}, opacity ${theme.timing.base} ${theme.transition.bounce}`,
            willChange: 'transform',
            transform: 'scale(0.95)',
            transformOrigin: 'center center',
          },

          '&:hover': {
            '::before': {
              opacity: 1,
              transform: 'scale(1)',
            },
          },

          [theme.mq.medium]: {
            gridAutoColumns: 'auto',
            gridAutoFlow: 'column',
            textAlign: 'left',
          },
        }}
      >
        <div
          css={{
            width: 80,
            height: 80,
            display: 'grid',
            placeContent: 'center',
            margin: 'auto',
          }}
        >
          <Section
            css={{
              width: 70,
              paddingTop: `0px !important`,
              paddingBottom: `0px !important`,
            }}
          >
            <Image src={serie.imageLink} />
          </Section>
        </div>
        <div css={{ display: 'grid', gap: theme.space.small }}>
          <div
            css={{
              ...theme.text.large,
              fontWeight: theme.fontWeight.bold,
            }}
          >
            <Data>{serie.heading}</Data>
          </div>
          <div css={{ ...theme.text.small }}>
            <Data>{serie.blurb}</Data>
          </div>
        </div>
      </motion.div>
    </a>
  )
}
Serie.propTypes = {
  serie: PropTypes.shape({
    blurb: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    link: PropTypes.string,
    imageLink: PropTypes.string,
  }),
}

const TitleAndCollection = ({ heading, blurb, collections }) => {
  const theme = useTheme()

  return (
    <Section
      space="large"
      css={{
        display: 'flex',
        flexDirection: 'column',
        [theme.mq.small__max]: {
          textAlign: 'center',
        },
      }}
    >
      <SectionTitle
        align={{ medium: 'center' }}
        title={<Data>{heading}</Data>}
        sub={<Data>{blurb}</Data>}
        css={{
          marginBottom: `0px !important`,
        }}
      />
      <Section
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'row',
          paddingTop: `0px !important`,
          paddingBottom: `0px !important`,
          marginBottom: `0px !important`,
          [theme.mq.small__max]: {
            flexDirection: 'column',
            alignItems: 'center',
          },
        }}
      >
        {collections.map((collection) => (
          <Section
            key={collection.heading}
            space="large"
            css={{
              maxWidth: 280,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingTop: `10px !important`,
              paddingBottom: `0px !important`,
              marginBottom: `0px !important`,
            }}
          >
            <Section
              css={{
                maxWidth: 200,
                paddingTop: `0px !important`,
                paddingBottom: `0px !important`,
              }}
            >
              <Image src={collection.imageLink} />
            </Section>
            <SectionTitle
              as="h4"
              align={{ medium: 'center' }}
              title={<Data>{collection.heading}</Data>}
              sub={<Data>{collection.blurb}</Data>}
              css={{
                paddingBottom: `0px !important`,
                marginBottom: `0px !important`,
              }}
            />
          </Section>
        ))}
      </Section>
    </Section>
  )
}
TitleAndCollection.propTypes = {
  heading: PropTypes.string.isRequired,
  blurb: PropTypes.string.isRequired,
  collections: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string.isRequired,
      imageLink: PropTypes.string,
      blurb: PropTypes.string.isRequired,
    })
  ),
}

const ModuleHorizontal = ({ heading, blurb, items }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'flex-start',
        paddingBottom: 100,
        position: 'relative',
        [theme.mq.small__max]: {
          flexDirection: 'column',
          alignItems: 'center',
          paddingBottom: `30px !important`,
        },
      }}
    >
      <div
        css={{
          position: 'absolute',
          maxWidth: 770,
          left: '-17rem',
          [theme.mq.small__max]: {
            position: 'relative',
            maxWidth: '100%',
            left: 'initial',
          },
          [theme.mq.medium__max]: {
            maxWidth: `400px !important`,
            left: `-1rem !important`,
          },
        }}
      >
        <Image src="https://res.cloudinary.com/gremlin/image/upload/v1634732136/Landing%20Pages/Certification%20Page/Gremlin-Certification-Certificate.png" />
      </div>
      <div
        css={{
          height: 450,
          width: 1300,
          [theme.mq.small__max]: {
            display: 'none',
            alignItems: 'center',
          },
        }}
      />
      <div css={{ alignSelf: 'end', paddingTop: 10 }}>
        <SectionTitle
          as="h2"
          align={{ medium: 'left' }}
          title={heading}
          css={{
            marginBottom: `${theme.space.large}px !important`,
          }}
        />
        <Data>{blurb}</Data>
        {items.map((item) => (
          <div key={item.heading} css={{ display: 'grid' }}>
            <div
              css={{
                ...theme.text.large,
                fontSize: 26,
                fontWeight: theme.fontWeight.bold,
                paddingTop: 20,
                paddingBottom: 20,
              }}
            >
              <Data>{item.heading}</Data>
            </div>
            <div css={{ ...theme.text.small }}>
              <Data>{item.blurb}</Data>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
ModuleHorizontal.propTypes = {
  heading: PropTypes.string.isRequired,
  blurb: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string.isRequired,
      imageLink: PropTypes.string,
      blurb: PropTypes.string.isRequired,
    })
  ),
}

const Banner = ({ heading }) => {
  const theme = useTheme()

  return (
    <Section
      space="base"
      css={{
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        paddingBottom: `100px !important`,
        paddingTop: `50px !important`,
      }}
    >
      <SectionTitle
        as="h2"
        align={{ medium: 'center' }}
        css={{ marginBottom: `${theme.space.large}px !important` }}
        title={<Data>{heading}</Data>}
      />
      <div
        css={{
          display: 'flex',
          maxWidth: 600,
          margin: 'auto',
          [theme.mq.small__max]: { flexDirection: 'column' },
        }}
      >
        <Button
          noContrast
          type="primary"
          url="https://www.gremlin.com/certification-practitioner/"
          css={{
            maxWidth: 350,
            margin: 'auto',
            marginRight: 10,
          }}
        >
          Get GCCEP certified
        </Button>
        <Button
          noContrast
          type="primary"
          url="https://www.gremlin.com/certification-professional/"
          css={{
            maxWidth: 350,
            margin: 'auto',
            marginLeft: 10,
            [theme.mq.small__max]: {
              marginTop: 20,
            },
          }}
        >
          Get GCCEPro certified
        </Button>
      </div>
    </Section>
  )
}
Banner.propTypes = {
  heading: PropTypes.string.isRequired,
}

const LastQuote = ({ author, title, content }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        width: '100%',
        overflow: 'hidden',
        backgroundColor: '#111111',
        '&::after': {
          bottom: theme.space.large,
        },

        [theme.mq.medium]: {
          display: 'flex',
          justifyContent: 'center',

          '&::after': {
            display: 'none',
          },
        },
      }}
    >
      <Section
        css={{ display: 'flex', justifyContent: 'center', maxWidth: 1000 }}
      >
        <Quote
          author={author}
          title={title}
          size="large"
          contrastQuotes
          css={{
            color: 'white',
            '& svg': {
              color: '#5FBF8B',
            },
            [theme.mq.small__max]: {
              paddingLeft: `30px !important`,
              paddingRight: `30px !important`,
            },
          }}
        >
          <Data>{content}</Data>
        </Quote>
        <HomeHeroGridImage
          css={{
            position: 'absolute',
            right: 0,
            bottom: 750,
            zIndex: 10,
            [theme.mq.small__max]: {
              bottom: 1462,
            },
          }}
        >
          <GridSVG
            css={{
              height: 650,
              [theme.mq.small__max]: {
                height: 300,
              },
            }}
          />
        </HomeHeroGridImage>
      </Section>
    </div>
  )
}
LastQuote.propTypes = {
  author: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
}

const NewCertification = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()
  const breakpoints = [576, 800, 1100, 1250, 1350]
  const mq = breakpoints.map((bp) => `@media (max-width: ${bp}px)`)

  return (
    <BaseLayout footerProps={{ hideCTA: true }}>
      <HelmetSEO title={pageData.title} description={pageData.description} />
      <Hero
        css={{
          background: '#1F1F1F',
          paddingBottom: 80,
          paddingTop: 30,
          [theme.mq.small__max]: {
            paddingTop: 80,
          },
        }}
      >
        <HomeHeroGrid css={{ position: 'relative' }}>
          <HomeHeroGridImage
            css={{
              position: 'absolute',
              right: '-21rem',
              top: '0rem',
              zIndex: 10,
              [mq[3]]: {
                right: '-10rem',
              },
              [mq[2]]: {
                right: '-3rem',
                width: 350,
              },
              [mq[1]]: {
                right: '0rem',
                width: 350,
              },
              [theme.mq.small__max]: {
                position: 'relative',
                right: `auto !important`,
                top: `auto !important`,
                textAlign: 'center',
                margin: 'auto',
              },
            }}
          >
            <AnimatedHero
              css={{
                height: `650px !important`,
                tranformOrigin: 'center',
                [mq[3]]: {
                  height: `400px !important`,
                },
                [mq[2]]: {
                  height: `100% !important`,
                },
                [mq[1]]: {
                  height: `100% !important`,
                },
                [theme.mq.medium]: {
                  height: `350px !important`,
                },
              }}
            />
          </HomeHeroGridImage>
          <HomeHeroGridContent
            css={{
              paddingTop: 0,
              paddingBottom: theme.space.large,
              [theme.mq.medium]: {
                gridColumn: '1 / span 7',
                paddingBottom: 0,
              },
            }}
          >
            <HeroTitle size="base" css={{ fontSize: 75 }}>
              <Data>{pageData.hero.heading}</Data>
            </HeroTitle>
            <h4
              css={{
                textTransform: 'uppercase',
                letterSpacing: theme.letterSpacing.extended,
                marginTop: 0,
                color: `${theme.color.primary.base} !important`,
              }}
            >
              <Data>{pageData.hero.meta}</Data>
            </h4>
            <HeroTitleSub>
              <Data>{pageData.hero.blurb}</Data>
            </HeroTitleSub>
            <div css={{ marginTop: theme.space.large }}>
              <Inline
                css={{
                  justifyContent: 'center',

                  [theme.mq.medium]: {
                    justifyContent: 'flex-start',
                  },
                }}
              >
                <ListItem>
                  <Button
                    noContrast
                    type="primary"
                    url="https://www.gremlin.com/certification-professional/"
                  >
                    Get GCCEPro certified
                  </Button>
                </ListItem>
              </Inline>
            </div>
          </HomeHeroGridContent>
        </HomeHeroGrid>
        <svg
          css={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: 100,
            display: 'none',
            zIndex: 2,
            transform: 'scaleX(-1)',
            [theme.mq.medium]: {
              display: 'block',
            },
          }}
          fill="#fff"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon
            css={{ filter: `drop-shadow(0 0 5px ${theme.color.dark.base})` }}
            points="0,100 100,0 100,100"
          />
        </svg>
      </Hero>
      <div
        css={{
          position: 'absolute',
          zIndex: -1,
          top: '50rem',
          transform: 'scaleY(0.82)',
          [mq[4]]: {
            display: `none !important`,
          },
        }}
      >
        <BackgroundSVG
          css={{
            height: '100%',
          }}
        />
      </div>
      <Container>
        {pageData.leftSection && <LeftSection {...pageData.leftSection} />}
        {pageData.titleAndCollection && (
          <TitleAndCollection {...pageData.titleAndCollection} />
        )}
        {pageData.gremlinSeries?.length &&
          pageData.gremlinSeries.map((gremlinSerie) => (
            <GremlinSeries key={gremlinSerie.heading} {...gremlinSerie} />
          ))}
        {pageData.moduleHorizontal && (
          <ModuleHorizontal {...pageData.moduleHorizontal} />
        )}
        {pageData.bannerText && <Banner {...pageData.bannerText} />}
        {pageData.titleAndCollection2 && (
          <TitleAndCollection {...pageData.titleAndCollection2} />
        )}
      </Container>
      {pageData.lastQuote && <LastQuote {...pageData.lastQuote} />}
    </BaseLayout>
  )
}

NewCertification.propTypes = {
  data: PropTypes.object.isRequired,
}

export default NewCertification

export const pageQuery = graphql`
  query CertificationPageData {
    file(name: { eq: "certification" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        frontmatter {
          title
          description
          hero {
            blurb
            heading
            meta
          }
          leftSection {
            heading
            blurb
          }
          titleAndCollection {
            heading
            blurb
            collections {
              heading
              imageLink
              blurb
            }
          }
          gremlinSeries {
            heading
            blurb
            items {
              heading
              blurb
              link
              imageLink
            }
          }
          moduleHorizontal {
            heading
            blurb
            items {
              heading
              blurb
            }
          }
          titleAndCollection2 {
            heading
            blurb
            collections {
              heading
              imageLink
              blurb
            }
          }
          bannerText {
            heading
          }
          lastQuote {
            author
            title
            content
          }
        }
      }
    }
  }
`
